import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import {
  useDeleteEncounter,
  useEncounterCards,
  useEncounterSheet,
} from '../../domains/encounters/queries'
import { useEncounterEditing } from '../../domains/encounters/useEncounterEditing'
import { CombatCard } from '../../domains/encounters/sections/CombatCard'
import { SidesCard } from '../../domains/encounters/sections/SidesCard'
import { LiveSessionCard } from '../../domains/live/LiveSessionCard'
import { LocationPicker } from '../../domains/encounters/sections/LocationPicker'
import { Banner, Button, Card, IconButton, Input, SectionLabel } from '../../design/components'
import styles from './play.module.css'

/**
 * One encounter: its sides, and — on request — every participant's combat card.
 *
 * **Game Master only, reads included.** A player arriving here gets a 403 from the API,
 * which this renders as the same empty state a missing encounter produces: the point of
 * the rule is that a player learns nothing, so "not yours" and "not there" have to look
 * identical.
 */
export function EncounterDetailPage() {
  const params = useParams<{ encounterId: string }>()
  const encounterId = Number(params.encounterId)

  const [showCards, setShowCards] = useState(false)

  const query = useEncounterSheet(encounterId)
  const cards = useEncounterCards(encounterId, showCards)
  const editing = useEncounterEditing(encounterId)

  if (Number.isNaN(encounterId)) {
    return <EncounterMessage message="That encounter id is not a number." />
  }

  if (query.isPending) {
    return <EncounterMessage message="Loading…" subtitle="FETCHING ENCOUNTER" />
  }

  if (query.isError) {
    const missing =
      query.error instanceof ApiError &&
      (query.error.kind === 'not-found' || query.error.kind === 'forbidden')

    return (
      <EncounterMessage
        message={
          missing
            ? 'No such encounter, or it is not one of yours. Encounters are the Game Master’s.'
            : 'Could not load this encounter.'
        }
        tone={missing ? 'info' : 'error'}
      />
    )
  }

  const sheet = query.data
  const core = sheet.sections.core

  return (
    <AppShell
      title={core?.name ?? 'Encounter'}
      subtitle={subtitleFor(core?.type, core?.sideCount, core?.participantCount)}
    >
      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      <SidesCard encounterId={encounterId} sides={sheet.sections.sides} editing={editing} />

      {/* Above the combat cards, because starting a session is what a Game Master came here to do
          once the sides are arranged — and below them it would be under N full character sheets. */}
      {core !== undefined && (
        <LiveSessionCard
          encounterId={encounterId}
          campaignId={core.campaignId}
          locationId={core.locationId}
          locationName={core.locationName}
          battleMapImageUrl={core.battleMapImageUrl}
          canStart={core.canStartLiveSession}
          liveSessionId={core.liveSessionId}
        />
      )}

      {/* The cards are N full character sheets, so they are a separate read and an
          explicit ask. Most encounter opens are about arranging the sides. */}
      <Card variant="powers" label="Combat cards" hint={showCards ? undefined : 'Not loaded'}>
        {!showCards ? (
          <>
            <p className={styles.note}>
              Every participant&rsquo;s sheet through a combat lens — skills, stress, consequences,
              powers and the worked-out modifiers.
            </p>
            <Button
              fullWidth
              disabled={(core?.participantCount ?? 0) === 0}
              onClick={() => setShowCards(true)}
            >
              {(core?.participantCount ?? 0) === 0 ? 'Nobody in play yet' : 'Load the cards'}
            </Button>
          </>
        ) : cards.isPending ? (
          <p className={styles.note}>Loading the cards…</p>
        ) : cards.isError ? (
          <Banner tone="error">Could not load the combat cards.</Banner>
        ) : (
          <p className={styles.note}>
            {(cards.data.sections.cards?.cards ?? []).length} card
            {(cards.data.sections.cards?.cards ?? []).length === 1 ? '' : 's'}.
          </p>
        )}
      </Card>

      {showCards &&
        cards.isSuccess &&
        (cards.data.sections.cards?.cards ?? []).map((card) => (
          <CombatCard
            key={`${card.characterId}-${card.shapeId ?? 'default'}`}
            card={card}
            lens={core?.type ?? 'Combat'}
          />
        ))}

      {core !== undefined && (
        <GameMasterCard encounterId={encounterId} core={core} editing={editing} />
      )}

      <Link to={`/campaigns/${sheet.campaignId}/encounters`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All encounters
        </Button>
      </Link>
    </AppShell>
  )
}

function GameMasterCard({
  encounterId,
  core,
  editing,
}: {
  encounterId: number
  core: {
    name: string
    notes: string
    type: 'Combat' | 'Social'
    campaignId: number
    locationId: number | null
    locationName: string | null
  }
  editing: ReturnType<typeof useEncounterEditing>
}) {
  const navigate = useNavigate()
  const remove = useDeleteEncounter()

  const [open, setOpen] = useState(false)
  const [name, setName] = useState(core.name)
  const [notes, setNotes] = useState(core.notes)
  const [confirming, setConfirming] = useState(false)

  return (
    <Card
      variant="stunts"
      label="Game Master"
      /* Glyphs in the card's action slot, as the city and location cards do. Full-width worded
         buttons for edit and delete put two of the loudest controls on the screen below the
         thing they act on, and "Delete encounter" in danger red was the largest element on a
         card whose usual job is renaming. */
      action={
        open ? undefined : (
          <>
            <IconButton label="Rename or add notes" icon="edit" onClick={() => setOpen(true)} />
            <IconButton
              label="Delete this encounter"
              icon="delete"
              onClick={() => setConfirming(true)}
            />
          </>
        )
      }
    >
      {/* The location picker sits on the card itself, not inside the edit form.
          It was behind "Rename or add notes" first, and that was wrong twice over: the label
          promises nothing about a location, and two other lines on this screen tell you to set
          one. A screen that says "say where it happens" three times and offers no control until
          you guess which button hides it is a screen with no control.

          It saves on change rather than on a Save press, because it is one select and there is
          nothing to review — unlike the name and notes, which are free text and keep their form. */}
      {/* The type sits beside the location for the same reason the location sits on the card: it
          is one press with nothing to review, and it changes what every combat card on this
          screen is read through — a social encounter shows social skills where a fight shows
          attacks. It used to be fixed at creation; it is not any more, because an encounter that
          turns into a conversation is an ordinary evening. */}
      {!open && (
        <div className={styles.field}>
          <SectionLabel tone="muted" micro>
            What kind of encounter
          </SectionLabel>
          <div className={styles.formActions}>
            {(['Combat', 'Social'] as const).map((type) => (
              <Button
                key={type}
                variant={core.type === type ? 'primary' : 'secondary'}
                disabled={editing.update.isPending || core.type === type}
                onClick={() =>
                  editing.update.mutate({
                    name: core.name,
                    notes: core.notes === '' ? null : core.notes,
                    type,
                  })
                }
              >
                {type}
              </Button>
            ))}
          </div>
        </div>
      )}

      {!open && (
        <LocationPicker
          campaignId={core.campaignId}
          value={core.locationId}
          disabled={editing.update.isPending}
          onChange={(next) =>
            editing.update.mutate({
              name: core.name,
              notes: core.notes === '' ? null : core.notes,
              // Three states on the wire: an omitted locationId means "leave it alone", so
              // removing one has to be said outright.
              ...(next === null
                ? core.locationId === null
                  ? {}
                  : { clearLocation: true }
                : { locationId: next }),
            })
          }
          hint={
            core.locationId === null
              ? 'Set this to run the fight as a live session everyone watches.'
              : undefined
          }
        />
      )}

      {core.notes !== '' && !open && <p className={styles.note}>{core.notes}</p>}

      {open ? (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            editing.update.mutate(
              // The location is not in this form; it saves itself from the card above.
              { name, notes: notes === '' ? null : notes },
              { onSuccess: () => setOpen(false) },
            )
          }}
        >
          <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
          <label className={styles.field}>
            <span className={styles.rowMeta}>Notes</span>
            <textarea
              className={styles.textarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setName(core.name)
                setNotes(core.notes)
                setOpen(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editing.update.isPending || name.trim() === ''}>
              {editing.update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : null}

      {/* The confirmation still spells the consequence out in words. A glyph is enough to *ask*
          for a delete; it is not enough to confirm one. */}
      {confirming && (
        <div className={styles.danger}>
          <p className={styles.note}>
            Deleting the encounter removes its sides and who stood on them. The characters
            themselves are untouched.
          </p>
          <div className={styles.formActions}>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(encounterId, {
                  onSuccess: () => void navigate(`/campaigns/${core.campaignId}/encounters`),
                })
              }
            >
              {remove.isPending ? 'Deleting…' : 'Delete encounter'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

function EncounterMessage({
  message,
  subtitle,
  tone = 'info',
}: {
  message: string
  subtitle?: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title="Encounter" {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link to="/campaigns" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All campaigns
        </Button>
      </Link>
    </AppShell>
  )
}

/** e.g. "COMBAT · 2 SIDES · 5 IN PLAY". */
function subtitleFor(
  type: string | undefined,
  sides: number | undefined,
  participants: number | undefined,
): string | undefined {
  const parts: string[] = []
  if (type !== undefined) parts.push(type)
  if (sides !== undefined) parts.push(sides === 1 ? '1 side' : `${sides} sides`)
  if (participants !== undefined) parts.push(`${participants} in play`)

  return parts.length === 0 ? undefined : parts.join(' · ')
}
